import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { CaretRight, Plus } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tally-style unit picker: click/typing filters a side "List of Units"
// panel (GST UQC master). Ctrl+A opens an inline Create Unit form
// (full name + abbreviation); the saved unit joins the list and is
// auto-selected. Self-fetches units from /api/units.
export const UnitPicker = ({ value, onChange, testid = "unit-picker", required = false }) => {
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newAbbr, setNewAbbr] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/units`).then((r) => setUnits(r.data.units || [])).catch(() => {});
  }, []);

  const filtered = q
    ? units.filter((u) =>
        u.abbreviation.toLowerCase().includes(q.toLowerCase()) ||
        u.name.toLowerCase().includes(q.toLowerCase()))
    : units;

  const close = () => { setOpen(false); setQ(""); setCreating(false); setErr(""); };

  const pick = (u) => {
    onChange(u.abbreviation);
    close();
  };

  const createUnit = async () => {
    const name = newName.trim();
    const abbr = newAbbr.trim().toUpperCase();
    if (!name || !abbr || busy) return;
    setBusy(true);
    setErr("");
    try {
      const r = await axios.post(`${API}/units`, { name, abbreviation: abbr });
      setUnits((us) => [...us, r.data].sort((a, b) => a.abbreviation.localeCompare(b.abbreviation)));
      pick(r.data);
    } catch (e2) {
      setErr(e2.response?.data?.detail || "Failed to create unit");
    }
    setBusy(false);
  };

  const onKey = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "a") {
      e.preventDefault();
      setOpen(true);
      setCreating(true);
      setNewAbbr(q.toUpperCase());
      setNewName("");
      setErr("");
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!open) setOpen(true);
      else setHi((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      if (open && !creating) {
        e.preventDefault();
        if (filtered[hi]) pick(filtered[hi]);
      }
    } else if (e.key === "Escape") {
      close();
    }
  };

  return (
    <div className="relative">
      <input
        data-testid={`${testid}-input`}
        value={open ? q : (value || "")}
        placeholder="Search units… (Ctrl+A: new unit)"
        onFocus={() => { setOpen(true); setQ(""); setHi(0); }}
        onChange={(e) => { setQ(e.target.value); setHi(0); if (!open) setOpen(true); }}
        onKeyDown={onKey}
        required={required && !value}
        className="w-full h-9 px-3 py-2 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-violet-500 text-sm"
        autoComplete="off"
      />
      {creating && (
        <div className="absolute left-0 right-0 top-full mt-1 z-40 p-3 bg-amber-50 border-2 border-amber-300 rounded-sm shadow-lg" data-testid={`${testid}-create-form`}>
          <p className="text-[11px] font-black uppercase tracking-wider text-amber-800 mb-1.5">Create New Unit</p>
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); createUnit(); }
              else if (e.key === "Escape") { e.stopPropagation(); setCreating(false); setErr(""); }
            }}
            placeholder="Unit full name (e.g. Kilograms)"
            autoFocus
            data-testid={`${testid}-create-name`}
            className="w-full px-2.5 py-1.5 border border-amber-300 rounded-sm text-sm mb-2"
            data-enter-nav-skip="true"
          />
          <input
            value={newAbbr}
            onChange={(e) => setNewAbbr(e.target.value.toUpperCase())}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); createUnit(); }
              else if (e.key === "Escape") { e.stopPropagation(); setCreating(false); setErr(""); }
            }}
            placeholder="Abbreviation (e.g. KGS)"
            maxLength={6}
            data-testid={`${testid}-create-abbr`}
            className="w-full px-2.5 py-1.5 border border-amber-300 rounded-sm text-sm mb-2 uppercase"
            data-enter-nav-skip="true"
          />
          {err && <p className="text-[11px] font-bold text-red-600 mb-2" data-testid={`${testid}-create-error`}>{err}</p>}
          <div className="flex gap-2">
            <button type="button" onClick={createUnit} disabled={busy || !newName.trim() || !newAbbr.trim()}
              data-testid={`${testid}-create-save`}
              className="flex-1 px-3 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-sm text-xs font-bold disabled:opacity-50 flex items-center justify-center gap-1">
              <Plus size={12} weight="bold" /> {busy ? "Saving…" : "Save Unit"}
            </button>
            <button type="button" onClick={() => { setCreating(false); setErr(""); }}
              data-testid={`${testid}-create-cancel`}
              className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs font-bold text-slate-600 hover:bg-slate-100">
              Cancel
            </button>
          </div>
        </div>
      )}
      {open && createPortal(
        <div
          className="fixed top-1/2 -translate-y-1/2 right-6 h-[80vh] w-72 bg-white border-2 border-violet-400 rounded-sm shadow-2xl z-[120] flex flex-col pointer-events-auto"
          data-group-picker-panel="true"
          data-testid={`${testid}-panel`}
        >
          <div className="px-4 py-2.5 bg-violet-900 text-white text-xs font-black uppercase tracking-widest text-center flex-shrink-0">
            List of Units
          </div>
          {q && !creating && (
            <div className="px-4 py-1.5 bg-violet-50 border-b border-violet-100 text-[11px] font-bold text-violet-800 flex-shrink-0" data-testid={`${testid}-filter-hint`}>
              "{q}" — {filtered.length} match{filtered.length === 1 ? "" : "es"}
            </div>
          )}
          <div className="flex-1 overflow-y-auto bg-violet-50/40">
            {filtered.length === 0 && !creating && (
              <p className="text-xs text-slate-400 text-center py-6">No matching unit — press Ctrl+A to create one</p>
            )}
            {filtered.map((u, i) => (
              <button
                key={u.id}
                type="button"
                ref={i === hi ? (el) => el && el.scrollIntoView({ block: "nearest" }) : undefined}
                onMouseDown={(e) => { e.preventDefault(); pick(u); }}
                onMouseEnter={() => setHi(i)}
                data-testid={`${testid}-option-${u.abbreviation}`}
                className={`w-full text-left px-4 py-1.5 text-sm flex items-center gap-2 ${
                  i === hi
                    ? "bg-violet-600 text-white font-bold"
                    : u.abbreviation === value
                      ? "bg-violet-100 text-violet-900 font-semibold"
                      : "text-slate-800 hover:bg-violet-100"
                }`}
              >
                {i === hi && <CaretRight size={11} weight="bold" />}
                <span className="w-12 font-black">{u.abbreviation}</span>
                <span className="flex-1 truncate">{u.name}</span>
              </button>
            ))}
          </div>
          <div className="px-4 py-1.5 bg-slate-50 border-t border-slate-200 text-[10px] font-semibold text-slate-500 text-center flex-shrink-0">
            ↑↓ Navigate · Enter Select · Ctrl+A New Unit
          </div>
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); close(); }}
            data-testid={`${testid}-close`}
            className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-900 hover:bg-slate-100 border-t border-slate-200 flex-shrink-0"
          >
            Close (Esc)
          </button>
        </div>,
        document.body
      )}
    </div>
  );
};
